import { ICON_ASSETS, ICON_PNG_DIR } from '@/config/iconAssets';
import type { IconSize } from '@/config/iconAssets';

/**
 * Which file an icon request actually resolves to.
 *
 * Callers throughout the desktop name an icon by its original SVG path, which
 * is the stable identifier. Where a real Windows XP set has been extracted for
 * that name, this swaps in the bitmap drawn for the size being asked for.
 *
 * That indirection is the whole point of the exercise. XP drew every icon size
 * separately — the 16px Recycle Bin is not the 48px one shrunk, it is its own
 * picture on its own pixel grid, with detail removed by hand so it still reads
 * at that size. Handing the browser one image and a `width` throws that away.
 */

export interface ResolvedIcon {
  src: string;
  /** Only set when a distinct 2× asset exists worth offering. */
  srcSet?: string;
}

/**
 * `/assets/icons/notepad.svg` → `notepad`. Anything else — `/assets/ui/*`, an
 * absolute URL, a site favicon — has no extracted set and passes through.
 */
const ICON_NAME = /\/assets\/icons\/([a-z0-9-]+)\.svg$/;

/**
 * Exact match first, then the smallest size *larger* than asked for, and the
 * largest available only as a last resort.
 *
 * The upward bias matters. Scaling 16px art up to 32 discards the detail the
 * 32px drawing was made to carry; scaling 24 down to 20 only resamples it. So
 * when there is no exact size, overshooting is the lesser loss.
 */
export function pickSize(sizes: readonly IconSize[], want: number): IconSize | undefined {
  return sizes.find((s) => s === want) ?? sizes.find((s) => s > want) ?? sizes.at(-1);
}

export function resolveIcon(src: string, size: number): ResolvedIcon {
  const name = ICON_NAME.exec(src)?.[1];
  if (!name) return { src };

  const sizes = ICON_ASSETS[name];
  if (!sizes || sizes.length === 0) return { src };

  const one = pickSize(sizes, size);
  if (one === undefined) return { src };

  const resolved = `${ICON_PNG_DIR}/${name}-${one}.png`;

  // On a 2× display a 16px icon is really 32 device pixels, and XP drew that
  // one too. Only offer it when it is genuinely a different file.
  const two = pickSize(sizes, size * 2);
  if (two !== undefined && two !== one) {
    return { src: resolved, srcSet: `${resolved} 1x, ${ICON_PNG_DIR}/${name}-${two}.png 2x` };
  }

  return { src: resolved };
}
